import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useSelectedStore } from '@web/app/shared';
import { ChevronDown, User } from 'lucide-react';

import { useOwnerStore } from '@codes/owner';
import { useStorageTransfer } from '@codes/storage-transfer';
import { useTheme } from '@codes/theme';
import { Avatar, AvatarFallback, AvatarImage, SharedSpinner, cn } from '@codes/ui-kit';
import { useLogout, useWebCoreStore } from '@codes/web-core';

import { ContactModal } from '../../../ContactModal';

export const UserBadge = () => {
    const [opened, setOpened] = useState(false);
    const { userName, profile } = useWebCoreStore();
    const { t, i18n } = useTranslation();
    const { theme, setTheme } = useTheme();
    const queryClient = useQueryClient();
    const ownerInfo = useOwnerStore(state => state.owner);
    const navigate = useNavigate();
    const [showContactModal, setShowContactModal] = useState(false);

    const target = import.meta.env.VITE_BOX_HOST?.toLowerCase();
    const { handleTransfer } = useStorageTransfer(target as string);

    const { clearSelection } = useSelectedStore();

    const [language, setLanguage] = useState<string>(i18n.language || 'en');

    const handleThemeChange = () => {
        setTheme(theme === 'light' ? 'dark' : 'light');
    };
    const toggleLanguage = () => {
        const newLanguage = language === 'en' ? 'ko' : 'en';
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);
    };

    const logoutMutation = useLogout(async () => {
        toast({
            title: t('auth.successLogout'),
            duration: 2000,
        });

        await queryClient.resetQueries();
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    });

    const onTransferClick = async () => {
        const result = await handleTransfer();
        if (result.success) {
            console.log('전송 성공!');
        } else {
            console.error('전송 실패:', result.error);
        }
    };

    if (!ownerInfo) {
        return <SharedSpinner />;
    }

    return (
        <>
            <DropdownMenu open={opened} onOpenChange={setOpened}>
                <DropdownMenuTrigger className="focus-visible:outline-0 md:bg-white md:dark:bg-[#424242] h-[50px] flex md:rounded-full py-3 px-4 md:border-[0.6px] md:border-gray-300 md:shadow-custom">
                    <div className="flex items-center gap-[10px]">
                        <Avatar className="w-[30px] h-[30px] border-0 bg-[#FFE8DA] dark:bg-[#3B2A22] p-0">
                            <AvatarImage src={profile?.$user?.photo} alt="@shadcn" />
                            <AvatarFallback className="bg-[#FFE8DA] dark:bg-[#3B2A22]">
                                <User className="w-[18px] h-[18px] text-point" />
                            </AvatarFallback>
                        </Avatar>
                        <div className="text-base flex items-center gap-1">
                            <span>{userName}</span>
                            <ChevronDown
                                className={cn(
                                    'w-[18px] h-[18px] transition-transform duration-200',
                                    opened && 'rotate-180'
                                )}
                            />
                        </div>
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[260px] pb-3">
                    <div>
                        <div className="text-lg font-medium">{userName}</div>
                        {!!ownerInfo?.email && <div className="text-sm text-placeholder">{ownerInfo.email}</div>}
                    </div>
                    <DropdownMenuSeparator className="-mx-4 bg-gray-300" />
                    <DropdownMenuLabel className="text-base py-0 mb-2">MY</DropdownMenuLabel>
                    <div className="bg-[#F4F5F5] dark:bg-[#3A3C40] rounded-md flex flex-col gap-1 py-2 px-[9px] mb-[6px]">
                        <DropdownMenuItem
                            className="text-sm font-normal p-0 hover:underline hover:bg-transparent"
                            onClick={() => navigate('/my-page?tab=info')}
                        >
                            {t('header.personalInfo')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            className="text-sm font-normal p-0 hover:underline hover:bg-transparent"
                            onClick={() => navigate('/my-page?tab=payment')}
                        >
                            {t('header.billing')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            className="text-sm font-normal p-0 hover:underline hover:bg-transparent"
                            onClick={() => navigate('/my-page?tab=subscriptions')}
                        >
                            {t('header.subscriptions')}
                        </DropdownMenuItem>
                        {/*<DropdownMenuItem*/}
                        {/*    className="text-sm font-normal p-0 hover:underline hover:bg-transparent"*/}
                        {/*    onClick={() => navigate('/access-keys')}*/}
                        {/*>*/}
                        {/*    {t('menu.accessKeys')}*/}
                        {/*</DropdownMenuItem>*/}
                    </div>
                    <DropdownMenuItem
                        className="text-base font-medium text-gray-800 py-1"
                        onClick={() => setShowContactModal(true)}
                    >
                        Contact Us
                    </DropdownMenuItem>
                    {/* <DropdownMenuItem className="flex items-center justify-center gap-[14px] mt-3 ">
                        <button onClick={handleThemeChange}>
                            {theme === 'dark' ? (
                                <Sun className="w-6 h-6  hover:text-icon-hover" strokeWidth={1.5} />
                            ) : (
                                <Moon className="w-6 h-6 hover:text-icon-hover" strokeWidth={1.5} />
                            )}
                        </button>
                        <button className="relative" onClick={toggleLanguage}>
                            <Image
                                className="w-6 h-6 block hover:opacity-0"
                                src={language === 'en' ? KrIcon : EnIcon}
                                darkSrc={language === 'en' ? KrDarkIcon : EnDarkIcon}
                                alt={language === 'en' ? 'Korean' : 'English'}
                            />
                            <Image
                                className="w-6 h-6 absolute top-0 left-0 opacity-0 hover:opacity-100"
                                src={language === 'en' ? KrHoverIcon : EnHoverIcon}
                                alt={language === 'en' ? 'Korean' : 'English'}
                            />
                        </button>
                    </DropdownMenuItem> */}
                    <DropdownMenuItem
                        className="text-base font-medium text-gray-800 py-1"
                        onClick={() => logoutMutation.mutate()}
                    >
                        {t('header.logout')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
        </>
    );
};
